import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { EuiButton, EuiFormRow, EuiPage, EuiPageBody, EuiPageHeader, EuiPageHeaderSection, EuiPanel, EuiSpacer, EuiText, EuiTitle } from '@elastic/eui'

import { useGetAIPlanBlueprintsQuery } from 'api/rtkQueryApi/platform/aiPlanBlueprintsApi'
import { useGetAIPlansQuery } from 'api/rtkQueryApi/platform/aiPlansApi'
import { RootState } from 'app/rootReducer'
import { reset } from 'features/planner/CreateAIPlanSlice'

import history from '../../services/HistoryService'

export const AIPlanWelcomePage: React.FC = () => {
  const dispatch = useDispatch()
  const { currentOrganization, currentAccount, isStaffUser, showInternalView } = useSelector((state: RootState) => state.app)
  const { data: plans } = useGetAIPlansQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })
  const { data: blueprints } = useGetAIPlanBlueprintsQuery({ organizationId: currentOrganization?.id ?? '', showHidden: isStaffUser && showInternalView })

  useEffect(() => {
    if (!currentAccount || !plans) {
      return
    }

    const accountPlans = plans.filter(p => p.accountId === currentAccount?.id)

    if (accountPlans.length <= 0) {
      return
    }

    dispatch(reset())
    history.push('/ai/plans')
  }, [currentAccount, dispatch, plans])

  const accountBlueprints = blueprints?.filter(b => b.accountId === currentAccount?.id) ?? []
  const hasBlueprints = accountBlueprints.length > 0

  const onCreateBlueprintClick = () => {
    history.push('/ai/blueprints/create')
  }

  const onCreatePlanClick = () => {
    history.push('/ai/plans/create')
  }

  return (
    <EuiPage className='euiPage--platform'>
      <EuiPageBody>
        <EuiPageHeader>
          <EuiPageHeaderSection>
            <EuiTitle>
              <h1>AI Plans</h1>
            </EuiTitle>
            <EuiText>Welcome to AI Plans!</EuiText>
          </EuiPageHeaderSection>
        </EuiPageHeader>
        <EuiPanel grow={false} className='eui-xScroll'>
          {hasBlueprints ? (
            <>
              <EuiTitle size={'s'}>
                <h2>Create Your First AI Plan</h2>
              </EuiTitle>
              <EuiSpacer />
              <EuiText>AI Plans use the market analysis and audience personas from your blueprints to build a complete, strategically targeted media plan.</EuiText>
              <EuiSpacer />
              <EuiText>You'll choose a blueprint, set your budget and channel preferences, and AdCritter AI will generate a detailed plan with campaigns ready to execute.</EuiText>
              <EuiSpacer />
              <EuiText>Once you've reviewed the plan, everything can be edited to your liking. When you're ready, a single click transfers it to the AdCritter DSP for execution.</EuiText>
              <EuiSpacer />
              <EuiFormRow>
                <EuiButton fill onClick={onCreatePlanClick}>
                  Let's get started!
                </EuiButton>
              </EuiFormRow>
            </>
          ) : (
            <>
              <EuiTitle size={'s'}>
                <h2>Blueprint Required</h2>
              </EuiTitle>
              <EuiSpacer />
              <EuiText>Before you can create an AI Plan, you need an AI Blueprint. Blueprints contain the market analysis and audience personas that power your plan.</EuiText>
              <EuiSpacer />
              <EuiText>Create a blueprint first by entering your client's company name and website URL. AdCritter AI will generate detailed market intelligence that you can then use to build strategically targeted campaigns.</EuiText>
              <EuiSpacer />
              <EuiFormRow>
                <EuiButton fill onClick={onCreateBlueprintClick}>
                  Create a Blueprint
                </EuiButton>
              </EuiFormRow>
            </>
          )}
        </EuiPanel>
      </EuiPageBody>
    </EuiPage>
  )
}
